import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useV2Auth } from '@/context/V2AuthContext';
import { useTranslation } from 'react-i18next';

/* Phase 4 — ruhiger globaler Rahmen: GEBEN · FÜR DICH · ICH, keine Account-Controls im Header */

const NAV_ITEMS = [
  { to: '/give', labelKey: 'v2.nav.give', testid: 'nav-give' },
  { to: '/momente', labelKey: 'v2.nav.momente', testid: 'nav-momente' },
  { to: '/inbox', labelKey: 'v2.nav.fuerdich', testid: 'nav-inbox', badge: true },
  { to: '/profile', labelKey: 'v2.nav.ich', testid: 'nav-profile' },
];

function UnreadBadge({ count }) {
  if (count <= 0) return null;
  return (
    <span
      data-testid="inbox-unread-badge"
      className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full text-[9px] font-medium leading-none"
      style={{ background: 'var(--text)', color: 'var(--bg)' }}
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

export default function V2Header() {
  const { user, API_V2 } = useV2Auth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) { setUnreadCount(0); return undefined; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API_V2}/carma/unread-count`, { withCredentials: true });
        if (cancelled) return;
        const c = res.data.count || 0;
        setUnreadCount(c);
        try {
          if ('setAppBadge' in navigator) {
            if (c > 0) navigator.setAppBadge(c); else navigator.clearAppBadge();
          }
        } catch (_e) { /* badging unsupported */ }
      } catch (_e) { /* keep last known count */ }
    };
    const delay = location.pathname === '/inbox' ? 1500 : 0;
    const timer = setTimeout(fetchCount, delay);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [user, API_V2, location.pathname]);

  const isActive = (to) => location.pathname === to
    || (to === '/inbox' && location.pathname === '/inbox-classic')
    || (to === '/profile' && (location.pathname === '/profile-classic' || location.pathname === '/tagebuch' || location.pathname === '/bewahrt'));

  return (
    <header className="relative z-40">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-5 pb-2 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate('/')}
          data-testid="header-logo-btn"
          className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_appreciation-flow/artifacts/gr67j2hw_Logo_Final_v1-removebg-preview.png"
            alt="CarmaCircle"
            className="w-[136px] sm:w-[168px] h-auto"
          />
        </button>

        {user ? (
          <nav className="flex items-center gap-4 sm:gap-7" data-testid="main-nav">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                aria-current={isActive(item.to) ? 'page' : undefined}
                className={`inline-flex items-center gap-1.5 py-2 text-[12px] sm:text-[13px] tracking-[0.1em] uppercase transition-colors ${isActive(item.to) ? 'text-[var(--text)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
              >
                {t(item.labelKey)}
                {item.badge && <UnreadBadge count={unreadCount} />}
              </Link>
            ))}
          </nav>
        ) : (
          <Link to="/auth" data-testid="nav-login" className="py-2 text-sm text-[var(--muted)] hover:text-[var(--text)] transition-colors">
            {t('nav.login')}
          </Link>
        )}
      </div>
    </header>
  );
}
